<script>
import Teleport from 'vue2-teleport';
import projectApi from '@/api/project';
import utils from '@/utils/common';
import DefaultLayout from '@/layouts/Default.vue';
import taskApi from '../../../api/task';
import stageApi from '../../../api/stage';
import Access from '../../../utils/access';

import CommonSection from '../../CommonSection.vue';
import TaskTable from '../TaskTable.vue';
import TaskFiltersContainer from '../TaskFiltersContainer.vue';
import CommonDropdown from '../../CommonDropdown.vue';
import BaseCheckbox from '../../BaseCheckbox.vue';
import TaskViewFooterBar from './TaskViewFooterBar.vue';
import BaseAccordion from '../../BaseAccordion.vue';
import PageScrollerTwoButtons from '../../PageScrollerTwoButtons.vue';
import BaseInput from '../../BaseInput.vue';

const headers = [
  { title: 'Задача',       className: 'task-title'                                                                                                }, // eslint-disable-line
  { title: 'Проект',       className: 'task-project cell'                                                                                         }, // eslint-disable-line
  { title: 'Статус',       className: 'task-status cell'                                                                                          }, // eslint-disable-line
  { title: 'Приоритет',    className: 'task-priority cell',  sortFn: (a, b) => a.priority - b.priority,              sortValue: 'priority'        }, // eslint-disable-line
  { title: 'Дата',         className: 'task-date cell',      sortFn: (a, b) => a.taskFinishTime - b.taskFinishTime,  sortValue: 'dateFinish'      }, // eslint-disable-line
  { title: 'Постановщик',  className: 'task-author cell'                                                                                          }, // eslint-disable-line
  { title: 'Исполнитель',  className: 'task-manager cell'                                                                                         }, // eslint-disable-line
];

export default {
  name: 'TaskView',
  components: {
    CommonSection,
    TaskTable,
    TaskFiltersContainer,
    CommonDropdown,
    BaseCheckbox,
    TaskViewFooterBar,
    BaseAccordion,
    PageScrollerTwoButtons,
    BaseInput,
    Teleport,
    DefaultLayout,
  },
  data() {
    return {
      tasks: [],
      projects: [],
      selectedTasks: [],
      expandedProjectIds: [],
      isGroupedByProjects: true,
      filters: {
        projects: [],
        authors: [],
        managers: [],
        observers: [],
        users: [],
        statuses: [],
        priorities: [],
        isClosed: false,
        isDateFinishOpen: false,
        taskName: '',
      },
    };
  },
  created() {
    this.loadEntities();
  },
  computed: {
    headers() {
      return headers.map(header => ({ header, sort: this.sort === header, order: this.order }));
    },

    tasksFiltered() {
      const projects = new Set(this.filters.projects);
      const authors = new Set(this.filters.authors);
      const managers = new Set(this.filters.managers);
      const statuses = new Set(this.filters.statuses);
      const priorities = new Set(this.filters.priorities);
      const observers = new Set(this.filters.observers);
      const users = new Set(this.filters.users);

      return this.tasks.filter((t) => (
        (!this.filters.isClosed || !t.isClosed)
        && (!this.filters.isDateFinishOpen || t.dateFinish)
        && (!this.filters.taskName || t.name.toLowerCase().includes(this.filters.taskName.toLowerCase()))
        && (!projects.size || projects.has(t.project['@id']))
        && (!authors.size || authors.has(t.author['@id']))
        && (!managers.size || managers.has(t.manager?.['@id']))
        && (!statuses.size || statuses.has(t.status))
        && (!priorities.size || priorities.has(t.priority))
        && (!observers.size || utils.haveIntersection(observers, t.membersToCompare))
        && (!users.size || utils.haveIntersection(users, t.usersToCompare))
      ));
    },

    tasksGrouped() {
      const groupedTasks = {};
      this.tasksFiltered.forEach(t => {
        const tasks = groupedTasks[t.project?.id] ?? [];
        tasks.push(t);
        groupedTasks[t.project?.id] = tasks;
      });

      return groupedTasks;
    },

    projectsFiltered() {
      const visibleProjects = new Set(this.tasksFiltered.map((t) => t.project?.id));
      return this.projects.filter((p) => visibleProjects.has(p.id));
    },

    projectIds() {
      return this.projectsFiltered.map(p => p.id);
    },
  },
  methods: {
    async loadEntities(params = {}) {
      const [tasksFetch, stagesFetch, projectsFetch] = await Promise.all([
        taskApi.fetchList({}, true),
        stageApi.fetchList({}, true),
        projectApi.fetchList({ isArchived: false }, true),
      ]);

      const stagesObject = {};
      const projectsObject = {};

      stagesFetch.items.forEach(s => {
        stagesObject[s.id] = s;
      });

      this.projects = projectsFetch.items.map(p => {
        const lightProject = {};

        lightProject['@id'] = p['@id'];
        lightProject.id = p.id;
        lightProject.author = p.author;
        lightProject.avatar = p.avatar;
        lightProject.name = p.name;
        lightProject.projectMembers = p.projectMembers;

        projectsObject[p['@id']] = lightProject;

        return lightProject;
      });

      this.tasks = tasksFetch.items.map(t => {
        const lightTask = {};
        const taskStage = stagesObject[t.stage?.id];
        const projectId = taskStage?.project;
        const taskProject = projectsObject[projectId];

        if (!taskProject) return {};

        const membersToCompare = new Set(t.members.map((m) => m['@id']));
        const usersToCompare = new Set([...membersToCompare, t.author['@id']]);

        if (t.manager) {
          usersToCompare.add(t.manager['@id']);
        }

        lightTask['@id'] = t['@id'];
        lightTask.id = t.id;
        lightTask.author = t.author;
        lightTask.dateStart = new Date(t.dateStart);
        lightTask.taskFinishTime = t.dateFinish ? new Date(t.dateFinish).getTime() : -1;
        lightTask.dateFinish = t.dateFinish ? new Date(t.dateFinish) : null;
        lightTask.isClosed = t.isClosed;
        lightTask.manager = t.manager;
        lightTask.members = t.members;
        lightTask.name = t.name;
        lightTask.desctiption = t.desctiption;
        lightTask.priority = t.priority;
        lightTask.stage = t.stage;
        lightTask.status = t.status;
        lightTask.project = taskProject;
        lightTask.membersToCompare = membersToCompare;
        lightTask.usersToCompare = usersToCompare;
        lightTask.canBeUpdated = new Access({ ...t, project: taskProject }, this.$store.state.auth.user).canUpdateTask(true);

        return lightTask;
      }).filter(t => t.project);

      const visibleProjectIds = new Set(this.tasks.map((t) => t.project?.id));
      this.projects = this.projects.filter((p) => visibleProjectIds.has(p.id));
      this.expandedProjectIds = params.expandedProjectIds ? params.expandedProjectIds : [this.projects?.[0]?.id];
      this.selectedTasks = params.selectedTasks ? params.selectedTasks : [];
    },

    toggleAllProjects() {
      this.expandedProjectIds = this.expandedProjectIds.length ? [] : this.projectIds;
    },

    toggleProjectsExpanded(projectId) {
      this.expandedProjectIds = this.isProjectExpanded(projectId)
        ? this.expandedProjectIds.filter(id => id !== projectId)
        : [projectId, ...this.expandedProjectIds];
    },

    isProjectExpanded(projectId) {
      return this.expandedProjectIds.includes(projectId);
    },

    toggleTask(taskToToggle) {
      const index = this.selectedTasks.findIndex((t) => t.id === taskToToggle.id);
      if (index > -1) {
        this.selectedTasks.splice(index, 1);
      } else {
        this.selectedTasks.push(taskToToggle);
      }
    },

    toggleTasksFromTable(tasksToToggle, projectId) {
      const otherTasks = this.selectedTasks.filter((t) => t.project.id !== projectId);
      const projectTasks = this.selectedTasks.filter((t) => t.project.id === projectId);

      this.selectedTasks = (projectTasks.length === tasksToToggle.length)
        ? [...otherTasks]
        : [...otherTasks, ...tasksToToggle];
    },

    toggleAllTasks() {
      this.selectedTasks = (this.selectedTasks.length === this.tasksFiltered.length)
        ? []
        : this.tasksFiltered;
    },

    changeFilters(value, field) {
      this.filters[field] = value;
    },

    getTitle(projectId) {
      return this.isProjectExpanded(projectId) ? 'Скрыть проект' : 'Показать проект';
    },

    toggleClosedTasks() {
      this.filters.isClosed = !this.filters.isClosed;
    },

    filterSelectedTasks(projectId) {
      return this.selectedTasks.filter((t) => t.project.id === projectId);
    },

    async update() {
      const expandedProjectIds = [...this.expandedProjectIds];
      const selectedTasks = [...this.selectedTasks];

      await this.loadEntities({ expandedProjectIds, selectedTasks });
    },

    async updateTask({ id, field, value }) {
      const task = this.tasks.find(t => t.id === id);
      await taskApi.update(task.id, { [field]: value?.['@id'] ?? value }, true);
      task[field] = value;
    },

    toggleGroupingByProjects() {
      if (!this.isGroupedByProjects) {
        const projectsBySelectedTasks = this.selectedTasks.map(t => t.project.id);
        this.expandedProjectIds = projectsBySelectedTasks.length ? projectsBySelectedTasks : [this.projects[0].id];
        this.isGroupedByProjects = true;
      } else {
        this.isGroupedByProjects = false;
      }
    },

    toggleTasksWithoutDateFinish() {
      this.filters.isDateFinishOpen = !this.filters.isDateFinishOpen;
    },
  },
};
</script>

<template lang="pug">
DefaultLayout
  CommonSection(v-if="projects.length && tasks.length" title="Мои задачи")
    template(v-slot:button)
      BaseInput.task-search-input(v-model="filters.taskName" iconName="search" label="Поиск")
    PageScrollerTwoButtons
    TaskFiltersContainer.filters(
      :projects="projects"
      :tasks="tasks"
      :filters="filters"
      :areAllProjectsCollapsed="expandedProjectIds.length === 0"
      :isGroupedByProjects="isGroupedByProjects"
      @toggleGroupingByProjects="toggleGroupingByProjects"
      @toggleTasksWithoutDateFinish="toggleTasksWithoutDateFinish"
      @changeFilters="changeFilters"
      @toggleProjects="toggleAllProjects"
      @toggleClosedTasks="toggleClosedTasks"
      @updateTasks="update"
      )
    TaskTable(v-if="!isGroupedByProjects" :tasks="tasksFiltered" :headers="headers" :selectedTasks="selectedTasks" @toggleTask="toggleTask" @toggleTasks="toggleAllTasks" @updateTask="updateTask")
    template(v-if="isGroupedByProjects")
      .projects-container
        .projects-view(v-for="project in projectsFiltered" :key="project.id")
          BaseAccordion(:opened="isProjectExpanded(project.id)" @toggle="toggleProjectsExpanded(project.id)")
            template(v-slot:header)
              router-link.project-name(:to="{ name: 'project-stages', params: { projectId: project.id } }" target='_blank') {{ project.name }}
            TaskTable(
              v-if="isProjectExpanded(project.id)"
              :tasks="tasksGrouped[project.id]"
              :headers="headers"
              :selectedTasks="filterSelectedTasks(project.id)"
              @toggleTask="toggleTask"
              @toggleTasks="toggleTasksFromTable($event, project.id)"
              @updateTask="updateTask"
              )
  template(v-slot:footer)
    TaskViewFooterBar(:tasks="tasksFiltered" :selectedTasks="selectedTasks" @toggleAllItems="toggleAllTasks" @update="update")
</template>

<style scoped lang="scss">
.filters {
  padding-bottom: 49px;
}

.task-search-input {
  width: 311px;
  max-width: 100%;

  &::v-deep .base-input__field {
    padding-right: 45px;
  }
}

.projects-container {
  display: flex;
  flex-direction: column;
  gap: 56px;
}

.projects-view {
  display: flex;
  flex-direction: column;
}

.projects-view-header {
  display: grid;
  align-items: center;
  grid-template-columns: max-content 1fr min-content;
  gap: 6px;
  padding-bottom: 26px;
}

.line {
  height: 1px;
  background-color: rgba(209, 217, 240, 1);
}

.show-hide-button {
  .icon {
    cursor: pointer;
    width: 43px;
    height: 38px;
    background: var(--color-super-light);
    stroke: var(--color-middle-gray);
    display: flex;
    justify-content: center;
    align-items: center;
    rotate: 180deg;

    &:hover {
      stroke: var(--color-accent);
    }
  }

  .reverse {
    rotate: 180deg;
  }
}

.project-name {
  color: var(--middle-gray, #788CA1);
  font-family: Geometria, sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 184.615% */
}
</style>
